<template>
  <div class="signup">
    <section>
      <a href="https://www.cardladder.com">
        <full-logo pro />
      </a>

      <div class="form-container">
        <div class="title secondary-font">
          <h3>Go Pro for only <span class="success">{{ topPriceDisplay }}</span></h3>
          <h5 v-if="!needsConsent">
            With a 7-day free trial!
          </h5>
          <br v-else />
        </div>

        <div v-if="hasUserContext" class="user-context">
          <align flex small-gap>
            <div class="logged-in-context">
              <icon class="icon" name="check_circle" />
              <span>Signed in with {{ loggedInProvider }}</span>
            </div>

            <icon-button icon="close" @click="clearAuth" />
          </align>
        </div>

        <div>
          <div v-if="!hasUserContext" class="account-creation">
            <line-separator>
              SETUP YOUR LOGIN
            </line-separator>

            <auth-form v-model="form" />

            <line-separator v-show="!hideSocialAuth">
              OR
            </line-separator>

            <social-auth v-show="!hideSocialAuth" type="signup" />
          </div>

          <div v-show="!isCollectorsSignup" class="payment-entry">
            <line-separator>
              ENTER YOUR PAYMENT
            </line-separator>

            <div v-show="!giftCardObject || !giftCardObject.valid" class="options">
              <div
                :class="['option', { active: subscriptionType === 'monthly' }]"
                @click="subscriptionType = 'monthly'"
              >
                <div :class="['price', { strikethrough: monthlyPrice !== monthly.price }]">
                  {{ monthly.priceDisplay }}
                </div>

                <div v-if="monthlyPrice !== monthly.price" class="price">
                  {{ $filters.currency(monthlyPrice) }}/mo
                </div>

                <div v-if="!hasValidPromo" class="save">
                  <small>MOST FLEXIBLE</small>
                </div>
              </div>

              <div
                :class="['option', { active: subscriptionType === 'annual' }]"
                @click="subscriptionType = 'annual'"
              >
                <div :class="['price', { strikethrough: annualPrice !== annual.price }]">
                  {{ annual.priceDisplay }}
                </div>

                <div v-if="annualPrice !== annual.price" class="price">
                  {{ $filters.currency(annualPrice) }}/yr
                </div>

                <div v-if="!hasValidPromo" class="save">
                  <small>SAVE 17%</small>
                </div>
              </div>
            </div>

            <div v-show="!checkingExistingPaymentMethod && !useExistingPayment" :class="['cc-container', { focus: elementFocus }]">
              <div id="card-element" class="MyCardElement" />
            </div>

            <align v-show="!checkingExistingPaymentMethod && useExistingPayment" flex class="existing-payment">
              <span>Use existing card x{{ existingLast4 }}</span>
              <icon-button icon="close" @click="useExistingPayment = false" />
            </align>

            <div v-show="!checkingExistingPaymentMethod && !useExistingPayment" class="stripe-image">
              <a href="https://stripe.com/privacy" target="_blank">
                <img
                  height="24"
                  alt="Powered by Stripe"
                  src="@/assets/powered-by-stripe-blurple.svg"
                />
              </a>
            </div>
          </div>

          <payment-promo-result
            v-if="showPromoResult"
            :promo="promoObject"
            :is-monthly="isMonthly"
            @clear="clearPromo"
          />

          <div v-if="needsConsent" class="needs-consent">
            <p>It looks like you've already had a free trial with us and we will charge your card immediately for this subscription. If you'd like to still sign up please agree to being charged immediately.</p>
            <checkbox
              v-model="duplicateTrialConsent"
              label="I agree to my card being charged immediately"
            />
          </div>

          <div class="legal">
            <align>
              <checkbox v-model="agreeToTerms" />

              <p>
                By starting your subscription, you are agreeing to our
                <router-link to="/tos" target="_blank">
                  terms of use
                </router-link>
                and <router-link to="/privacy" target="_blank">
                  privacy policy
                </router-link>.
                <span>{{ pricingDescription }}</span>
              </p>
            </align>
          </div>

          <btn
            block
            :loading-text="loadingText"
            :loading="loading"
            :disabled="loading || !agreeToTerms"
            @click="submit"
          >
            {{ submitButtonText }}
          </btn>

          <div>
            <div class="links">
              <a v-if="hasUserContext" class="link" @click="logout">Logout</a>

              <a
                v-else
                class="link"
                @click="$router.push({ path: '/login', query: $route.query })"
              >
                Back to login
              </a>

              <span>|</span>

              <a
                class="link"
                @click="paymentPromoEntryOpen = true"
              >
                Have a promo code?
              </a>

              <span v-if="isMonthly">|</span>

              <a
                v-if="isMonthly"
                class="link"
                @click="giftCardEntryOpen = true"
              >
                Have a gift card?
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="banner">
      <div>
        <h1 class="secondary-font">
          With Card Ladder Pro,<br />you can:
        </h1>

        <div class="bullets">
          <align>
            <icon class="large-check" name="check_circle" />

            <h3 class="secondary-font">
              View all-time sales history for millions of cards
            </h3>
          </align>

          <align>
            <icon class="large-check" name="check_circle" />

            <h3 class="secondary-font">
              Upload your collection and get daily value estimates
            </h3>
          </align>

          <align>
            <icon class="large-check" name="check_circle" />

            <h3 class="secondary-font">
              Check estimated pricing for any card
            </h3>
          </align>

          <align>
            <icon class="large-check" name="check_circle" />

            <h3 class="secondary-font">
              View Full population reports for all graded cards
            </h3>
          </align>
        </div>

        <h3 class="secondary-font">
          And so much more...
        </h3>
      </div>
    </section>

    <portal to="fullscreen">
      <payment-promo-entry
        :open="paymentPromoEntryOpen"
        @close="paymentPromoEntryOpen = false"
        @submit="setPromo"
      />

      <gift-card-entry
        :open="giftCardEntryOpen"
        @close="giftCardEntryOpen = false"
        @submit="setGiftCard"
      />
    </portal>
  </div>
</template>

<script>
import firebase from 'firebase/compat/app';
import { mapState } from 'vuex';
import * as Sentry from '@sentry/vue';

import FullLogo from '@cardladder/shared/components/FullLogo';

import AuthForm from './AuthForm';
import SocialAuth from './SocialAuth';

import PaymentPromoResult from './PaymentPromoResult';
import PaymentPromoEntry from './PaymentPromoEntry';

import GiftCardEntry from './GiftCardEntry';

const VIA_ANNUAL_DEFAULTS = ['king', 'scl'];

export default {
  components: {
    AuthForm,
    SocialAuth,
    PaymentPromoResult,
    PaymentPromoEntry,
    GiftCardEntry,
    FullLogo,
  },

  data() {
    return {
      form: {
        email: '',
        password: '',
      },
      subscriptionType: 'monthly',
      monthly: {
        price: 20,
        priceDisplay: `${this.$filters.currency(20)}/mo`,
        priceId: import.meta.env.VITE_STRIPE_MONTHLY_PRICE_ID,
      },
      annual: {
        price: 200,
        priceDisplay: `${this.$filters.currency(200)}/yr`,
        priceId: import.meta.env.VITE_STRIPE_ANNUAL_PRICE_ID,
      },
      rewardful: null,
      stripe: null,
      cardElement: null,
      submitting: false,
      applyingPromo: false,
      elementFocus: false,
      promo: null,
      promoObject: null,
      paymentPromoEntryOpen: false,
      giftCardEntryOpen: false,
      giftCardObject: null,
      checkingExistingPaymentMethod: false,
      existingLast4: '',
      useExistingPayment: false,
      needsConsent: false,
      duplicateTrialConsent: false,
      agreeToTerms: false,
    };
  },

  created() {
    if (this.$route.query.via && VIA_ANNUAL_DEFAULTS.includes(this.$route.query.via)) {
      this.subscriptionType = 'annual';
    }

    if (this.$route.query.promo || this.$route.query.via) {
      this.promo = this.$route.query.promo || this.$route.query.via;
      this.applyPromo();
    }
  },

  mounted() {
    if (this.user && this.user.customerId) {
      this.getExistingPaymentMethod();
    }

    this.setupStripeElements();

    window.rewardful('ready', () => {
      if (window.Rewardful.referral) {
        this.rewardful = window.Rewardful.referral;
      }
    });

    if (this.hasUserContext) {
      console.log(`User ID: ${this.user.uid}`);
    }
  },

  destroyed() {
    this.cardElement.destroy();
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
    }),

    hasUserContext() {
      return Boolean(this.user && this.user.uid);
    },

    loggedInProvider() {
      if (!this.hasUserContext) {
        return null;
      }

      if (this.user.provider === 'google.com') {
        return 'Google';
      }

      if (this.user.provider === 'apple.com') {
        return 'Apple';
      }

      if (this.user.email) {
        return this.user.email;
      }

      return 'email and password';
    },

    loadingText() {
      return this.submitting ? 'Subscribing' : 'Applying Promo';
    },

    loading() {
      return this.submitting || this.applyingPromo;
    },

    subscriptionObject() {
      return this[this.subscriptionType];
    },

    isMonthly() {
      return this.subscriptionType === 'monthly';
    },

    hideSocialAuth() {
      return this.isCollectorsSignup || this.form.email || this.form.password;
    },

    hasValidPromo() {
      return this.promoObject && this.promoObject.active && this.promoObject.coupon && this.promoObject.coupon.valid;
    },

    isAnnualPromo() {
      return this.hasValidPromo && this.promoObject.annual;
    },

    monthlyPrice() {
      let { price } = this.monthly;

      if (this.hasValidPromo) {
        if (this.promoObject.coupon.percent_off) {
          price -= price * (this.promoObject.coupon.percent_off / 100);
        } else if (this.promoObject.coupon.amount_off) {
          price -= (this.promoObject.coupon.amount_off / 100);
        }
      }

      return price;
    },

    annualPrice() {
      let { price } = this.annual;

      if (this.isAnnualPromo) {
        if (this.promoObject.coupon.percent_off) {
          price -= price * (this.promoObject.coupon.percent_off / 100);
        } else if (this.promoObject.coupon.amount_off) {
          price -= (this.promoObject.coupon.amount_off / 100);
        }
      }

      return price;
    },

    showPromoResult() {
      if (this.isMonthly) {
        return this.hasValidPromo;
      }

      return this.isAnnualPromo;
    },

    selectedPrice() {
      return this.isMonthly ? this.monthlyPrice : this.annualPrice;
    },

    topPriceDisplay() {
      return `$${this.selectedPrice}/${this.isMonthly ? 'mo' : 'yr'}`;
    },

    disableAuthForm() {
      return this.loggedInWithSocial || this.hasUserContext;
    },

    isCollectorsSignup() {
      const email = this.form?.email || this.user?.email || null;

      return Boolean(email && (email.endsWith('@collectors.com') || email.endsWith('@gosgc.com')));
    },

    submitButtonText() {
      return this.isCollectorsSignup || this.giftCardObject || this.needsConsent ? 'Join Card Ladder' : 'Start free trial';
    },

    pricingDescription() {
      let { price } = this.subscriptionObject;
      let description = `Your ${this.subscriptionType} plan will be `;

      const priceDisplay = this.$filters.currency(price);

      if (!this.hasValidPromo || (!this.isMonthly && !this.isAnnualPromo)) {
        description += this.subscriptionObject.priceDisplay;
      } else {
        if (this.promoObject.coupon.percent_off) {
          price -= price * (this.promoObject.coupon.percent_off / 100);
        } else if (this.promoObject.coupon.amount_off) {
          price -= (this.promoObject.coupon.amount_off / 100);
        }

        description += `${this.$filters.currency(price)}`;

        if (this.promoObject.coupon.duration === 'forever') {
          description += ' forever';
        } else if (this.promoObject.coupon.duration === 'repeating') {
          description += ` for the first ${this.promoObject.coupon.duration_in_months} ${this.promoObject.coupon.duration_in_months === 1 ? 'month' : 'months'} and then ${priceDisplay} after that`;
        } else if (this.promoObject.coupon.duration === 'once') {
          description += ` for 1 ${this.isMonthly ? 'month' : 'year'} and then ${priceDisplay} after that`;
        }
      }

      if (!this.hasValidPromo || this.promoObject.coupon.duration !== 'forever' || price !== 0) {
        description += ' and will automatically renew until you cancel. No refunds apply.';
      } else {
        description += '.';
      }

      return description;
    },
  },

  methods: {
    async submit() {
      this.$toasted.clear();

      if (!this.submitting) {
        try {
          this.submitting = true;
          this.promoError = false;

          // eslint-disable-next-line no-undef
          const recaptchaToken = await grecaptcha.enterprise.execute(import.meta.env.VITE_RECAPTCHA_SITE_KEY, { action: 'signup' });

          if (this.isCollectorsSignup) {
            this.signup(null, recaptchaToken);
          } else if (this.useExistingPayment) {
            this.signup('existing', recaptchaToken);
          } else {
            this.stripe.createPaymentMethod({
              type: 'card',
              card: this.cardElement,
              billing_details: {
                email: this.hasUserContext ? this.user.email : this.form.email,
              },
            }).then((result) => {
              if (result.error) {
                this.$toasted.error(result.error.message);
                this.submitting = false;
              } else {
                this.signup(result.paymentMethod.id, recaptchaToken);
              }
            });
          }
        } catch (e) {
          this.submitting = false;
          this.promoError = false;
          this.$toasted.error('Verification failed. Please try again.');
        }
      }
    },

    async signup(paymentMethod, recaptchaToken) {
      try {
        const signupRequest = {
          promo: this.isMonthly || this.isAnnualPromo ? this.promoObject?.id : null,
          priceId: this.subscriptionObject.priceId,
          paymentMethod,
          rewardful: this.rewardful,
          subscriptionType: this.subscriptionType,
          recaptchaToken,
        };

        if (this.hasUserContext) {
          signupRequest.uid = this.user.uid;
          signupRequest.email = this.user.email;
        } else {
          signupRequest.email = this.form.email;
          signupRequest.password = this.form.password;
        }

        if (this.needsConsent && this.duplicateTrialConsent) {
          signupRequest.duplicateTrialConsent = true;
        }

        const { data: { user, subscription } } = await firebase.functions().httpsCallable(import.meta.env.VITE_SIGNUP_ENDPOINT)(signupRequest);

        if (user) {
          if (!this.hasUserContext) {
            await firebase.auth().signInWithEmailAndPassword(this.form.email, this.form.password);
          }

          await this.$store.dispatch('boot', user);
        }

        if (this.isCollectorsSignup) {
          this.handleSuccess(null);
        } else {
          if (subscription && subscription.latest_invoice) {
            const { latest_invoice: latestInvoice } = subscription;
            const { payment_intent: paymentIntent } = latestInvoice;

            if (paymentIntent) {
              const { client_secret: clientSecret, status } = paymentIntent;

              if (status === 'requires_action') {
                const confirmation = await this.stripe.confirmCardPayment(clientSecret);

                if (confirmation.error) {
                  this.$toasted.error(confirmation.error.message);
                } else {
                  this.handleSuccess(subscription);
                }
              } else if (status === 'requires_payment_method') {
                this.$toasted.error('Your card was declined');
              } else {
                this.handleSuccess(subscription);
              }
            } else {
              this.handleSuccess(subscription);
            }
          } else {
            this.$toasted.error('There was a problem processing your payment. Please try again.');
          }
        }

        this.needsConsent = false;
        this.duplicateTrialConsent = false;
      } catch (e) {
        if (e.code === 'functions/aborts') {
          this.$router.replace('/dashboard');
        } else if (e.code === 'functions/resource-exhausted') {
          this.needsConsent = true;
        } else if (e.code === 'functions/already-exists') {
          try {
            await firebase.auth().signInWithEmailAndPassword(this.form.email, this.form.password);
            await this.$store.dispatch('boot', firebase.auth().currentUser);
            this.$router.replace('/dashboard');
          } catch (e) {
            this.$toasted.error('Account already exists. Please login.');
            this.$router.replace('/login');
          }
        } else if (e.message) {
          this.$toasted.error(e.message);
        } else if (e.error?.message) {
          this.$toasted.error(e.error.message);
        } else if (e.response?.data?.raw?.message) {
          this.$toasted.error(e.response.data.raw.message);
        } else {
          this.$toasted.error('There was a problem signing up. Please try again.');
        }

        Sentry.captureException(e);
      }

      this.submitting = false;
    },

    async handleSuccess(subscription) {
      if (this.giftCardObject && this.giftCardObject.code && this.isMonthly) {
        await firebase.functions().httpsCallable('httpGiftUpGiftCardRedeem')({ code: this.giftCardObject.code });
      }

      if (subscription) {
        try {
          this.$gtm.trackEvent({ event: 'Subscribe' });
          // eslint-disable-next-line no-undef
          fbq('track', 'Purchase', { value: this.selectedPrice, currency: 'USD' });
        } catch (e) {
          // swallow
        }
      }

      this.$router.replace('/dashboard?welcome=true');
    },

    setupStripeElements() {
      // eslint-disable-next-line no-undef
      this.stripe = Stripe(import.meta.env.VITE_STRIPE_KEY);
      const elements = this.stripe.elements({
        fonts: [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=fallback' }],
      });

      const style = {
        base: {
          color: '#2d3331',
          fontFamily: 'Lato, sans-serif',
          fontSize: '14px',
          '::placeholder': {
            color: '#96a39e',
            fontWeight: '700',
          },
          iconColor: '#34be8b',
        },
        invalid: {
          color: '#ED4949',
          iconColor: '#ED4949',
        },
      };

      this.cardElement = elements.create('card', {
        iconStyle: 'solid',
        style,
      });

      this.cardElement.mount('#card-element');

      this.cardElement.on('focus', () => {
        this.elementFocus = true;
      });

      this.cardElement.on('blur', () => {
        this.elementFocus = false;
      });
    },

    setPromo(promo) {
      this.promo = promo;
      this.applyPromo();
    },

    clearPromo() {
      this.promo = null;
      this.promoObject = null;
      this.giftCardObject = null;
    },

    setGiftCard(giftCard) {
      this.giftCardObject = giftCard;
      this.subscriptionType = 'monthly';
      this.setPromo(giftCard.stripePromo);
    },

    async applyPromo() {
      this.promoObject = null;
      this.applyingPromo = true;

      try {
        const { data: promoObject } = await firebase.functions().httpsCallable(import.meta.env.VITE_STRIPE_PROMO_RETRIEVE_ENDPOINT)({
          promo: this.promo,
        });

        this.promoObject = promoObject;

        if (this.promoObject.active && this.promoObject.coupon && this.promoObject.coupon.valid) {
          this.subscriptionType = promoObject.annual ? 'annual' : 'monthly';
        } else {
          this.promo = null;
        }
      } catch (e) {
        this.promoObject = { active: false };
        this.promo = null;
      }

      this.applyingPromo = false;
    },

    async getExistingPaymentMethod() {
      this.checkingExistingPaymentMethod = true;

      try {
        const { data: customerObject } = await firebase.functions().httpsCallable(import.meta.env.VITE_STRIPE_CUSTOMER_RETRIEVE_ENDPOINT)({ customerId: this.user.customerId });

        if (customerObject && customerObject.invoice_settings && customerObject.invoice_settings.last4) {
          this.existingLast4 = customerObject.invoice_settings.last4;
          this.useExistingPayment = true;
        }
      } catch (e) {
        // swallow
      }

      this.checkingExistingPaymentMethod = false;
    },

    async clearAuth() {
      this.useExistingPayment = false;
      this.needsConsent = false;
      this.duplicateTrialConsent = false;
      await this.logout(false);
    },

    async logout(route = true) {
      try {
        this.$store.dispatch('logout');
        await firebase.auth().signOut();

        if (route) {
          this.$router.replace('/login');
        }
      } catch (e) {
        this.$toasted.error('Error logging out');
        Sentry.captureException(e);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.signup {
  min-height: 100%;
  display: grid;
  grid-template-columns: 1fr;

  @media($medium-up) {
    grid-template-columns: 1fr 1.75fr;
  }

  > section {
    padding: $gp;

    @media($medium-up) {
      padding: $gp * 2;
    }
  }
}

.form-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 500px;
  margin: $gp * 2 auto;
}

.user-context {
  margin-bottom: $gp;
  width: 100%;
}

.account-creation {
  margin-bottom: $gp * 2;
}

.title {
  width: 100%;

  h1,
  h2,
  h3:not(:last-child) {
    margin-bottom: 0;
  }
}

.price {
  font-size: 20px;
  font-weight: bold;

  &.strikethrough {
    text-decoration: line-through;
    font-size: 12px;
  }
}

.logged-in-context {
  border: 1px solid var(--theme-border);
  border-radius: $border-radius;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;

  span {
    font-weight: bold;
    margin-left: 5px;
    font-size: 14px;
  }

  .icon {
    color: $color-green-1;
  }
}

.cc-container {
  margin: $gp 0 $gp * .5;
  padding: $gp * .5;
  border: 1px solid var(--theme-border);
  border-radius: $border-radius;
  color: var(--theme-color);

  &.focus {
    border-color: $color-green-1;
  }
}

.existing-payment {
  margin-bottom: $gp;
}

.options {
  display: flex;
  margin-bottom: $gp;

  .option {
    cursor: pointer;
    height: 84px;
    padding: $gp;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    border-radius: $border-radius;
    border: 1px solid var(--theme-border);
    flex: 1;

    &.active {
      background-color: var(--theme-active);
      border-color: $color-green-1;
    }

    &:not(:last-child) {
      margin-right: $gp;
    }

    &:not(.active):hover {
      background-color: var(--theme-highlight);
    }
  }
}

.stripe-image {
  text-align: center;
  margin: $gp 0;
}

.needs-consent {
  margin: $gp 0;

  p {
    font-size: 14px;
  }
}

.legal {
  font-size: 12px;
  margin: $gp 0;

  p {
    line-height: 1.4;
  }

  a:not(.link) {
    text-decoration: underline;
  }
}

.links {
  margin-top: $gp;
  font-size: 12px;
  text-align: center;

  a {
    cursor: pointer;
    padding: 0 4px;

    &:hover {
      text-decoration: underline;
    }
  }
}

.banner {
  background-image: url('@/assets/signup-banner.png');
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $color-white;

  @media($medium-down) {
    display: none;
  }

  .large-check {
    font-size: 48px;
    margin-bottom: $gp;
  }

  .bullets {
    margin: $gp * 2 0;
  }
}
</style>
